import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { fetchConfig, resetShimmer, yieldToMainThread, onAnimationEnd } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Quick order list block component.
 * Quantities are collected and submitted all at once via a dedicated "Add to cart" button,
 * instead of being sent to the cart on each quantity change.
 *
 * @extends Component
 */
class QuickOrderListBlockComponent extends Component {
  requiredRefs = ['errorContainer', 'errorText'];

  /** @type {AbortController|null} */
  #abortController = null;

  /** @type {(event: Event) => void} */
  #boundHandleCartUpdate;

  /** @type {number[]} */
  #resetTimeouts = [];

  /** @type {boolean} */
  #isLoading = false;

  /** @type {() => void} */
  #boundUpdateSubmitState;

  connectedCallback() {
    super.connectedCallback();
    this.#boundHandleCartUpdate = this.#handleCartUpdate.bind(this);
    this.#boundUpdateSubmitState = this.#updateSubmitState.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this.#boundHandleCartUpdate);

    // Submit buttons stay disabled until at least one variant has a quantity > 0.
    // `input` covers typing (the quantity selector only dispatches its update event on blur).
    this.addEventListener('input', this.#boundUpdateSubmitState);
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#boundUpdateSubmitState);

    // cart-quantity-selector-component re-enables buttons dynamically on init,
    // overriding the server-rendered disabled state. Re-apply after all children connect.
    requestAnimationFrame(() => {
      this.#disableUnavailableQuantitySelectors();
      this.#updateSubmitState();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#boundHandleCartUpdate);
    this.removeEventListener('input', this.#boundUpdateSubmitState);
    this.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#boundUpdateSubmitState);
    this.#abortController?.abort();
  }

  /**
   * Called when the form is submitted (via the "Add to cart" button).
   * Collects all variant quantities > 0 and sends them to the cart in one request.
   *
   * @param {unknown} _data
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    const items = this.#collectItems();
    if (items.length === 0) return;

    this.#clearMessages();
    this.#setLoading(true);

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const body = JSON.stringify({
        items,
        sections: this.#getSectionIds().join(','),
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', { body }),
        signal: this.#abortController.signal,
      });

      const data = await response.json();

      this.#setLoading(false);

      if (data.status) {
        this.#showError(data.description || data.message);
      } else {
        this.#animateFlyToCart();
        this.#animateButton();

        document.dispatchEvent(
          new CartAddEvent(data, this.id, {
            source: 'quick-order-block',
            sections: data.sections,
          })
        );
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.#setLoading(false);
        resetShimmer(this);
        throw error;
      }
    }
  }

  /**
   * Enables the "Add to cart" button and the quote app button only when at least one quantity is > 0.
   * The quote button is injected by the app outside this block, so its state is also mirrored
   * via the `data-has-quantity` attribute for CSS (covers a button injected after this runs).
   */
  #updateSubmitState() {
    const hasQuantity = this.#collectItems().length > 0;
    this.toggleAttribute('data-has-quantity', hasQuantity);

    const btn = this.querySelector('[data-quick-order-submit]');
    if (btn instanceof HTMLButtonElement) {
      btn.disabled = this.#isLoading || !hasQuantity;
    }

    document.querySelectorAll('.omgrfq-btn-quotes').forEach((quoteBtn) => {
      if (quoteBtn instanceof HTMLButtonElement) quoteBtn.disabled = !hasQuantity;
      quoteBtn.setAttribute('aria-disabled', String(!hasQuantity));
    });
  }

  /**
   * Re-disables buttons and input of quantity selectors in unavailable rows.
   * Needed because cart-quantity-selector-component overrides server-disabled state on init.
   */
  #disableUnavailableQuantitySelectors() {
    const disabledCells = this.querySelectorAll('[data-quantity-disabled="true"]');
    for (const cell of disabledCells) {
      cell.querySelectorAll('button, input').forEach((el) => {
        if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) {
          el.disabled = true;
        }
      });
    }
  }

  /**
   * Collects all variant IDs and their quantities from the table.
   * Uses one input per variant row to avoid counting mobile/desktop duplicates twice.
   *
   * @returns {{ id: number, quantity: number }[]}
   */
  #collectItems() {
    const items = [];
    const seen = new Set();

    const rows = this.querySelectorAll('.quick-order-list__grid-row[data-variant-id]');
    for (const row of rows) {
      if (!(row instanceof HTMLElement)) continue;
      const variantId = row.dataset.variantId;
      if (!variantId || seen.has(variantId)) continue;
      seen.add(variantId);

      const input = row.querySelector('input[type="number"][data-cart-quantity]');
      if (!(input instanceof HTMLInputElement)) continue;

      const quantity = parseInt(input.value) || 0;
      if (quantity > 0) {
        items.push({ id: parseInt(variantId), quantity });
      }
    }

    return items;
  }

  /**
   * Refreshes the component when another part of the page updates the cart.
   * @param {CustomEvent} event
   */
  async #handleCartUpdate(event) {
    if (event.detail?.source === 'quick-order-block' && event.detail?.sourceId === this.id) {
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    if (this.dataset.sectionId) {
      await sectionRenderer.renderSection(this.dataset.sectionId, { cache: false });
    }
  }

  /**
   * @param {boolean} loading
   */
  #setLoading(loading) {
    this.#isLoading = loading;
    this.#updateSubmitState();
    this.classList.toggle('quick-order-list-disabled', loading);
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    this.refs.errorText.textContent = message;
    this.refs.errorContainer.classList.remove('hidden');
  }

  #clearMessages() {
    this.refs.errorContainer.classList.add('hidden');
  }

  /**
   * Animates the submit button with the checkmark burst, matching the buy-buttons add-to-cart animation.
   */
  async #animateButton() {
    const btn = this.querySelector('[data-quick-order-submit]');
    if (!(btn instanceof HTMLButtonElement)) return;

    this.#resetTimeouts.forEach((id) => clearTimeout(id));
    this.#resetTimeouts = [];

    btn.dataset.added = 'true';

    await yieldToMainThread();
    await onAnimationEnd(btn);

    const timeoutId = window.setTimeout(() => {
      btn.removeAttribute('data-added');
      this.#resetTimeouts = this.#resetTimeouts.filter((id) => id !== timeoutId);
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  }

  /**
   * Animates a product image flying from the submit button to the cart icon.
   * Only runs if the add_to_cart_animation theme setting is enabled.
   */
  #animateFlyToCart() {
    const btn = this.querySelector('[data-quick-order-submit]');
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !(btn instanceof HTMLButtonElement) || !image) return;
    if (this.dataset.addToCartAnimation !== 'true') return;

    const flyToCartElement = /** @type {any} */ (document.createElement('fly-to-cart'));
    flyToCartElement.classList.add('fly-to-cart--main');
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = btn;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * @returns {string[]}
   */
  #getSectionIds() {
    const ids = [];
    if (this.dataset.sectionId) ids.push(this.dataset.sectionId);

    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId && !ids.includes(el.dataset.sectionId)) {
        ids.push(el.dataset.sectionId);
      }
    });

    return ids;
  }
}

if (!customElements.get('quick-order-list-block-component')) {
  customElements.define('quick-order-list-block-component', QuickOrderListBlockComponent);
}
